import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useAuth } from './lib/auth.js';
import { canSeeConsoleSection, consoleSections, type ConsoleSectionPath } from './lib/consoleSections.js';
import { AppShell } from './components/layout/AppShell.js';
import { AdminShell } from './components/layout/AdminShell.js';
import { LoadingPane } from './components/ui.js';
import { SignIn } from './pages/SignIn.js';
import { Dashboard } from './pages/Dashboard.js';
import { CoursePage } from './pages/CoursePage.js';
import { SessionPage } from './pages/SessionPage.js';
import { CapstonePage } from './pages/CapstonePage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { GuidePage, GuideSectionPage, GuidesHome } from './pages/GuidesPage.js';

// The console is a separate concern from learning; most learners never open it,
// so it is not in the first bundle.
const AdminCourses = lazy(() => import('./admin/CoursesPage.js'));
const AdminCourseSettings = lazy(() => import('./admin/CourseSettingsPage.js'));
const AdminSessionBuilder = lazy(() => import('./admin/SessionBuilderPage.js'));
const AdminCapstoneBuilder = lazy(() => import('./admin/CapstoneBuilderPage.js'));
const AdminUsers = lazy(() => import('./admin/UsersPage.js'));
const AdminAnalytics = lazy(() => import('./admin/AnalyticsPage.js'));
const AdminAudit = lazy(() => import('./admin/AuditLogPage.js'));
const AdminObservability = lazy(() => import('./admin/ObservabilityPage.js'));
const AdminGuides = lazy(() => import('./admin/GuidesAdminPage.js'));
const AdminGuideEditor = lazy(() => import('./admin/GuideEditorPage.js'));

export function App() {
  const { state } = useAuth();

  if (state.status === 'loading') {
    return (
      <div className="min-h-full grid place-items-center bg-canvas">
        <LoadingPane label="Checking your session…" />
      </div>
    );
  }

  if (state.status === 'anonymous') {
    return (
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="*" element={<RedirectToSignIn />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/signin" element={<Navigate to="/" replace />} />

      <Route
        path="/"
        element={
          <AppShell>
            <Dashboard />
          </AppShell>
        }
      />
      <Route
        path="/courses/:courseSlug"
        element={
          <AppShell>
            <CoursePage />
          </AppShell>
        }
      />
      <Route
        path="/courses/:courseSlug/sessions/:sessionSlug"
        element={
          <AppShell>
            <SessionPage />
          </AppShell>
        }
      />
      <Route
        path="/courses/:courseSlug/capstone"
        element={
          <AppShell>
            <CapstonePage />
          </AppShell>
        }
      />

      {/* Guides are open to every signed-in user — no enrollment, no console. */}
      <Route
        path="/guides"
        element={
          <AppShell>
            <GuidesHome />
          </AppShell>
        }
      />
      <Route
        path="/guides/:sectionSlug"
        element={
          <AppShell>
            <GuideSectionPage />
          </AppShell>
        }
      />
      <Route
        path="/guides/:sectionSlug/:guideSlug"
        element={
          <AppShell>
            <GuidePage />
          </AppShell>
        }
      />

      <Route path="/admin" element={<AdminRoute />} />
      <Route path="/admin/courses" element={<AdminRoute section="/admin/courses"><AdminCourses /></AdminRoute>} />
      <Route
        path="/admin/courses/:courseId"
        element={<AdminRoute section="/admin/courses"><AdminCourseSettings /></AdminRoute>}
      />
      <Route
        path="/admin/courses/:courseId/sessions/:sessionId"
        element={<AdminRoute section="/admin/courses"><AdminSessionBuilder /></AdminRoute>}
      />
      <Route
        path="/admin/courses/:courseId/capstone"
        element={<AdminRoute section="/admin/courses"><AdminCapstoneBuilder /></AdminRoute>}
      />
      <Route path="/admin/users" element={<AdminRoute section="/admin/users"><AdminUsers /></AdminRoute>} />
      <Route path="/admin/analytics" element={<AdminRoute section="/admin/analytics"><AdminAnalytics /></AdminRoute>} />
      <Route path="/admin/audit" element={<AdminRoute section="/admin/audit"><AdminAudit /></AdminRoute>} />
      <Route
        path="/admin/observability"
        element={<AdminRoute section="/admin/observability"><AdminObservability /></AdminRoute>}
      />
      <Route path="/admin/guides" element={<AdminRoute section="/admin/guides"><AdminGuides /></AdminRoute>} />
      <Route path="/admin/guides/:guideId" element={<AdminRoute section="/admin/guides"><AdminGuideEditor /></AdminRoute>} />

      <Route
        path="*"
        element={
          <AppShell>
            <NotFoundPage />
          </AppShell>
        }
      />
    </Routes>
  );
}

/**
 * A console route renders only when its section is one the caller can see (the
 * same rule as the console nav); otherwise it sends them to the first section
 * they do have, or home when they have none. Without `section` it is the bare
 * /admin entry point and only does that redirect. What they can actually do
 * inside a section is still decided by the server on every call.
 */
function AdminRoute({ section, children }: { section?: ConsoleSectionPath; children?: ReactNode }) {
  const { state } = useAuth();
  if (state.status !== 'authenticated') return <Navigate to="/signin" replace />;

  const { capabilities } = state.me;
  if (!section || !canSeeConsoleSection(capabilities, section)) {
    const first = consoleSections(capabilities)[0];
    return <Navigate to={first ? first.to : '/'} replace />;
  }

  return (
    <AdminShell>
      <Suspense fallback={<LoadingPane label="Loading console…" />}>{children}</Suspense>
    </AdminShell>
  );
}

function RedirectToSignIn() {
  const location = useLocation();
  const target =
    location.pathname === '/'
      ? '/signin'
      : `/signin?redirectTo=${encodeURIComponent(location.pathname + location.search)}`;
  return <Navigate to={target} replace />;
}
